// One world coordinate system drives artwork, hit targets, lantern and camera.
export const JOURNEY_ART = "/assets/egypt/territory-v2.webp";
export const JOURNEY_SPRITES = "/assets/egypt/explorers-v2.webp";
export const JOURNEY_PREFS_KEY = "kd9-journey-v2";
export const PORTAL_TRANSIT_KEY = "kd9-portal-transit";

export const JOURNEY_DESTINATIONS = Object.freeze([
  { key: "diagnostics", label: "Diagnósticos", place: "Pirámides del conocimiento", description: "Explora los patrones que conectan tu historia y tu proceso personal.", to: "/diagnosticos", x: .111, y: .49, accent: "#ecc477", glyph: "△", frequencies: [82.41, 123.47] },
  { key: "academy", label: "Academia KD9", place: "El umbral de la esfinge", description: "Un lugar para aprender, profundizar y poner el conocimiento en práctica.", to: "/academia", x: .308, y: .53, accent: "#a8dcec", glyph: "𓂀", frequencies: [196, 293.66, 392] },
  { key: "temple", label: "Templo Vivo", place: "El corazón del territorio", description: "Encuentra a la comunidad y comparte encuentros en vivo con nuestros anfitriones.", to: "/app/templo-vivo", x: .502, y: .49, accent: "#d6b7f0", glyph: "◈", frequencies: [65.41, 98, 130.81] },
  { key: "daily", label: "Energía diaria", place: "El obelisco del día", description: "Haz una pausa y descubre la orientación simbólica de tu jornada.", x: .566, y: .60, accent: "#efcf8a", glyph: "☼", frequencies: [216, 324, 432] },
  { key: "support", label: "Acompañamiento", place: "El santuario del encuentro", description: "Un espacio cercano para encontrar escucha, orientación y acompañamiento.", to: "/acompanamiento", x: .70, y: .52, accent: "#b8ddc8", glyph: "❋", frequencies: [220, 277.18, 329.63] },
  { key: "market", label: "Marketplace", place: "El mercado ancestral", description: "Descubre creaciones y herramientas que acompañan tu camino.", to: "/tienda", x: .90, y: .54, accent: "#e8b69c", glyph: "◇", frequencies: [392, 523.25, 659.25] },
]);

export const destinationFor = (key) => JOURNEY_DESTINATIONS.find((item) => item.key === key) || JOURNEY_DESTINATIONS[2];
export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export function cameraFor(destination, viewport, entering = false) {
  const width = Math.max(1, viewport.width);
  const height = Math.max(1, viewport.height);
  const worldHeight = Math.max(height, width / 3);
  const worldWidth = worldHeight * 3;
  const scale = entering ? 1.46 : 1.035;
  const x = clamp(width * .5 - destination.x * worldWidth * scale, width - worldWidth * scale, 0);
  const y = clamp(height * .46 - destination.y * worldHeight * scale, height - worldHeight * scale, 0);
  return { width: worldWidth, height: worldHeight, scale, x, y };
}

export function stepDestination(key, direction) {
  const index = JOURNEY_DESTINATIONS.findIndex((item) => item.key === key);
  return JOURNEY_DESTINATIONS[(Math.max(0, index) + direction + JOURNEY_DESTINATIONS.length) % JOURNEY_DESTINATIONS.length].key;
}

export function isInteractiveTarget(target) {
  return Boolean(target?.isContentEditable || target?.closest?.("button, a, input, textarea, select, summary, dialog, [role='button'], [role='tab']"));
}

export function swipeDirection(start, end) {
  if (!start || !end) return 0;
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.abs(dx) >= 46 && Math.abs(dx) > Math.abs(dy) * 1.3 ? (dx < 0 ? 1 : -1) : 0;
}

export function readJourneyPrefs(preview = false) {
  if (preview || typeof window === "undefined") return {};
  try {
    const value = JSON.parse(window.localStorage.getItem(JOURNEY_PREFS_KEY) || "{}");
    return {
      avatar: ["man", "woman"].includes(value.avatar) ? value.avatar : window.sessionStorage.getItem("kd9-egypt-avatar"),
      selectedKey: destinationFor(value.selectedKey || window.sessionStorage.getItem("kd9-egypt-landmark")).key,
      illuminated: value.illuminated === true,
      calm: value.calm === true,
    };
  } catch { return {}; }
}

export function writeJourneyPrefs(value, preview = false) {
  if (preview || typeof window === "undefined") return;
  try { window.localStorage.setItem(JOURNEY_PREFS_KEY, JSON.stringify(value)); } catch { /* Private browsing can disable storage. */ }
}

export function initialJourney(config, preview) {
  const prefs = readJourneyPrefs(preview);
  return {
    avatar: ["man", "woman"].includes(prefs.avatar) ? prefs.avatar : config.show_avatar_selector === false ? "man" : "",
    selectedKey: destinationFor(prefs.selectedKey).key,
    phase: "idle", duration: 0, direction: 1,
    illuminated: prefs.illuminated === true,
    calm: prefs.calm === true,
  };
}

export function journeyReducer(state, action) {
  switch (action.type) {
    case "choose": return { ...state, avatar: action.avatar, phase: action.quiet ? "idle" : "arrived" };
    case "select": {
      if (state.phase === "entering") return state;
      const current = destinationFor(state.selectedKey);
      const next = destinationFor(action.key);
      const distance = next.x - current.x;
      return { ...state, avatar: state.avatar || "man", selectedKey: next.key, phase: action.quiet || next.key === current.key ? "arrived" : "walking", direction: distance < 0 ? -1 : 1, duration: clamp(Math.abs(distance) * 1400, 420, 1100) };
    }
    case "arrive": return state.phase === "walking" ? { ...state, phase: "arrived" } : state;
    case "settle": return ["arrived", "awakening"].includes(state.phase) ? { ...state, phase: "idle" } : state;
    case "light": return state.phase === "entering" ? state : { ...state, illuminated: !state.illuminated, phase: !state.illuminated && !action.quiet ? "awakening" : "idle" };
    case "calm": return { ...state, calm: !state.calm, phase: state.phase === "entering" ? "entering" : "idle" };
    case "enter": return { ...state, phase: "entering" };
    case "cancel": return { ...state, phase: "idle" };
    case "avatar": return { ...state, avatar: "", phase: "idle" };
    default: return state;
  }
}

export function ambienceFor(mode = "auto", hour = new Date().getHours()) {
  if (["dawn", "day", "dusk", "night"].includes(mode)) return mode;
  return hour >= 6 && hour < 10 ? "dawn" : hour >= 10 && hour < 17 ? "day" : hour >= 17 && hour < 19 ? "dusk" : "night";
}

export function liveDestinations(data) {
  return (Array.isArray(data?.consultants) ? data.consultants : []).filter((item) => item.status === "live" && item.session_id);
}

export function rememberPortal(destination) {
  if (!destination.to) return;
  try { window.sessionStorage.setItem(PORTAL_TRANSIT_KEY, JSON.stringify({ key: destination.key, at: Date.now() })); } catch {}
}

export function readPortal(pathname) {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(PORTAL_TRANSIT_KEY) || "null");
    if (!value || Date.now() - value.at > 120000) { window.sessionStorage.removeItem(PORTAL_TRANSIT_KEY); return null; }
    const destination = JOURNEY_DESTINATIONS.find((item) => item.key === value.key && item.to === pathname);
    if (destination) window.sessionStorage.removeItem(PORTAL_TRANSIT_KEY);
    return destination || null;
  } catch { return null; }
}
